import { Form } from 'antd'
import { type FC, useCallback, useState } from 'react'
import { Controller } from 'react-hook-form'
import { FaChevronDown, FaChevronRight, FaTimes } from 'react-icons/fa'

import {
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  DateFormatter,
  NormalizedTimePicker
} from '@/components/elements'
import { LABELS } from '@/constants/common/message.constants'
import { safeParseISO } from '@/libs/utils'
import { useTimeRestriction } from '@/utils/use-time-restriction'

import { type ShipmentOrderCardNamePrefix } from './shipment-order-card-form-values'
import { useShipmentOrderCardField } from './use-shipment-order-card-field'

type Props = {
  namePrefix?: ShipmentOrderCardNamePrefix
  canEditFields: boolean
  /** 閲覧専用表示（インポート未許可）で表示する既存値 */
  sourceOrder?: {
    departureTimeRestrictionStart: string | null
    departureTimeRestrictionEnd: string | null
    arrivalTimeRestrictionStart: string | null
    arrivalTimeRestrictionEnd: string | null
  }
}

/**
 * 発着時間制限の入力行（開閉トグル + 発地/着地の開始・終了）。
 * 使用頻度が低いためトグルで開閉する。既存値がある場合のみ初期表示する。
 */
export const TimeRestrictionRows: FC<Props> = ({
  namePrefix,
  canEditFields,
  sourceOrder
}) => {
  const { name, watchField, form } = useShipmentOrderCardField(namePrefix)
  const { setValue, setError, clearErrors, getValues, trigger } = form

  const shippingDateWatchValue = watchField('shippingDate') as
    | Date
    | string
    | null
  const deliveryDateWatchValue = watchField('deliveryDate') as
    | Date
    | string
    | null
  const departureTimeStartWatch = watchField(
    'departureTimeRestrictionStart'
  ) as string | null
  const departureTimeEndWatch = watchField('departureTimeRestrictionEnd') as
    | string
    | null
  const arrivalTimeStartWatch = watchField('arrivalTimeRestrictionStart') as
    | string
    | null
  const arrivalTimeEndWatch = watchField('arrivalTimeRestrictionEnd') as
    | string
    | null

  const [isTimeRestrictionVisible, setIsTimeRestrictionVisible] = useState(
    Boolean(
      departureTimeStartWatch ||
        departureTimeEndWatch ||
        arrivalTimeStartWatch ||
        arrivalTimeEndWatch
    )
  )

  const {
    isSameDayDelivery,
    departureStartRestriction,
    departureEndRestriction,
    arrivalStartRestriction,
    arrivalEndRestriction
  } = useTimeRestriction({
    shippingDate: shippingDateWatchValue,
    deliveryDate: deliveryDateWatchValue,
    departureStart: departureTimeStartWatch,
    departureEnd: departureTimeEndWatch,
    arrivalStart: arrivalTimeStartWatch,
    arrivalEnd: arrivalTimeEndWatch
  })

  /**
   * 時間制限4項目をまとめて再検証する。値を連鎖更新したあとに必ず呼ぶ。
   *
   * `setValue` は既定で検証を走らせないため、終了の自動補完を挟むと、保存ボタンが見る
   * `isValid` が補完前（＝開始だけ入力された不正な状態）のまま固定される。
   * また4項目は相互に依存する規則（同日のとき発地の終了は着地の開始以降にできない等）を
   * 持つため、1項目だけ検証すると他項目のエラー表示が古いまま残る。
   */
  const revalidateTimeRestrictions = () => {
    void trigger([
      name('departureTimeRestrictionStart'),
      name('departureTimeRestrictionEnd'),
      name('arrivalTimeRestrictionStart'),
      name('arrivalTimeRestrictionEnd')
    ])
  }

  const disabledDepartureStartTime = useCallback(
    () => departureStartRestriction,
    [departureStartRestriction]
  )

  // 発地の到着時間制限(終了)の無効化ロジック
  const disabledDepartureEndTime = useCallback(
    () => departureEndRestriction,
    [departureEndRestriction]
  )

  // 着地の到着時間制限(開始)の無効化ロジック
  const disabledArrivalTimeStart = useCallback(
    () => arrivalStartRestriction,
    [arrivalStartRestriction]
  )

  // 着地の到着時間制限(終了)の無効化ロジック
  const disabledArrivalTimeEnd = useCallback(
    () => arrivalEndRestriction,
    [arrivalEndRestriction]
  )

  return (
    <>
      {/* 発地の到着時間制限 */}
      {/* 発着時間制限は使用頻度が低いためトグルで開閉する */}
      {canEditFields && (
        <BasicTr>
          <BasicTd colSpan={4}>
            <Button
              type="link"
              className="!h-auto !px-0"
              icon={
                isTimeRestrictionVisible ? (
                  <FaChevronDown />
                ) : (
                  <FaChevronRight />
                )
              }
              onClick={() => setIsTimeRestrictionVisible((prev) => !prev)}
            >
              {LABELS.DEPARTURE_ARRIVAL_TIME_RESTRICTION}
            </Button>
          </BasicTd>
        </BasicTr>
      )}
      {(!canEditFields || isTimeRestrictionVisible) && (
        <>
          <BasicTr>
            <BasicTh className="whitespace-pre-line align-top">
              {LABELS.DEPARTURE_TIME_RESTRICTION_START}
            </BasicTh>
            <BasicTd className="align-top" colSpan={3}>
              {canEditFields ? (
                <div className="flex items-end gap-2">
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.START}</label>
                    <Controller
                      control={form.control}
                      name={name('departureTimeRestrictionStart')}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                          label=""
                        >
                          <NormalizedTimePicker
                            format="HH:mm"
                            placeholder={LABELS.START_TIME}
                            showNow={false}
                            needConfirm={false}
                            allowClear={false}
                            className="w-full"
                            minuteStep={5}
                            value={safeParseISO(field.value)}
                            disabledTime={disabledDepartureStartTime}
                            onInvalidText={(msg) => {
                              setValue(
                                name('departureTimeRestrictionStart'),
                                null,
                                {
                                  shouldValidate: false,
                                  shouldDirty: true
                                }
                              )
                              setError(
                                name('departureTimeRestrictionStart'),
                                { type: 'manual', message: msg },
                                { shouldFocus: true }
                              )
                            }}
                            onValidText={() =>
                              clearErrors(name('departureTimeRestrictionStart'))
                            }
                            onChange={(date) => {
                              const isoString = date ? date.toISOString() : null
                              field.onChange(isoString)

                              const currentEndString = getValues(
                                name('departureTimeRestrictionEnd')
                              )
                              const currentEndDate =
                                safeParseISO(currentEndString)

                              if (
                                date &&
                                (!currentEndDate || currentEndDate < date)
                              ) {
                                setValue(
                                  name('departureTimeRestrictionEnd'),
                                  isoString
                                )
                              }

                              if (isSameDayDelivery && date) {
                                const arrivalStartDate = safeParseISO(
                                  arrivalTimeStartWatch
                                )
                                if (
                                  arrivalStartDate &&
                                  date > arrivalStartDate
                                ) {
                                  setValue(
                                    name('arrivalTimeRestrictionStart'),
                                    null
                                  )
                                  setValue(
                                    name('arrivalTimeRestrictionEnd'),
                                    null
                                  )
                                }
                              }

                              revalidateTimeRestrictions()
                            }}
                          />
                        </Form.Item>
                      )}
                    />
                  </div>
                  <span className="flex-none pt-7">〜</span>
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.END}</label>
                    <Controller
                      control={form.control}
                      name={name('departureTimeRestrictionEnd')}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                          label=""
                        >
                          <NormalizedTimePicker
                            format="HH:mm"
                            placeholder={LABELS.END_TIME}
                            showNow={false}
                            needConfirm={false}
                            allowClear={false}
                            className="w-full"
                            minuteStep={5}
                            value={safeParseISO(field.value)}
                            disabledTime={disabledDepartureEndTime}
                            onInvalidText={(msg) => {
                              setValue(
                                name('departureTimeRestrictionEnd'),
                                null,
                                {
                                  shouldValidate: false,
                                  shouldDirty: true
                                }
                              )
                              setError(
                                name('departureTimeRestrictionEnd'),
                                { type: 'manual', message: msg },
                                { shouldFocus: true }
                              )
                            }}
                            onValidText={() =>
                              clearErrors(name('departureTimeRestrictionEnd'))
                            }
                            onChange={(date) => {
                              const isoString = date ? date.toISOString() : null
                              field.onChange(isoString)

                              revalidateTimeRestrictions()
                            }}
                          />
                        </Form.Item>
                      )}
                    />
                  </div>
                  {/* 削除ボタンコンテナ */}
                  <div className="mb-[1px] flex h-[32px] w-8 flex-none items-center justify-center">
                    {(departureTimeStartWatch || departureTimeEndWatch) && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        className="hover:bg-red-50 flex items-center justify-center p-1"
                        onClick={() => {
                          setValue(name('departureTimeRestrictionStart'), null)
                          setValue(name('departureTimeRestrictionEnd'), null)
                          revalidateTimeRestrictions()
                        }}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-end gap-2">
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.START}</label>
                    {sourceOrder?.departureTimeRestrictionStart ? (
                      <DateFormatter
                        value={sourceOrder.departureTimeRestrictionStart}
                        dateFormat="HH:mm"
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.END}</label>
                    {sourceOrder?.departureTimeRestrictionEnd ? (
                      <DateFormatter
                        value={sourceOrder.departureTimeRestrictionEnd}
                        dateFormat="HH:mm"
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              )}
            </BasicTd>
          </BasicTr>

          <BasicTr>
            <BasicTh className="whitespace-pre-line align-top">
              {LABELS.ARRIVAL_TIME_RESTRICTION_START}
            </BasicTh>
            <BasicTd className="align-top" colSpan={3}>
              {canEditFields ? (
                <div className="flex items-end gap-2">
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.START}</label>
                    <Controller
                      control={form.control}
                      name={name('arrivalTimeRestrictionStart')}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                          label=""
                        >
                          <NormalizedTimePicker
                            format="HH:mm"
                            placeholder={LABELS.START_TIME}
                            showNow={false}
                            needConfirm={false}
                            allowClear={false}
                            className="w-full"
                            minuteStep={5}
                            value={safeParseISO(field.value)}
                            disabledTime={disabledArrivalTimeStart}
                            onInvalidText={(msg) => {
                              setValue(
                                name('arrivalTimeRestrictionStart'),
                                null,
                                {
                                  shouldValidate: false,
                                  shouldDirty: true
                                }
                              )
                              setError(
                                name('arrivalTimeRestrictionStart'),
                                { type: 'manual', message: msg },
                                { shouldFocus: true }
                              )
                            }}
                            onValidText={() =>
                              clearErrors(name('arrivalTimeRestrictionStart'))
                            }
                            onChange={(date) => {
                              const isoString = date ? date.toISOString() : null
                              field.onChange(isoString)

                              const currentEndString = getValues(
                                name('arrivalTimeRestrictionEnd')
                              )
                              const currentEndDate =
                                safeParseISO(currentEndString)
                              if (
                                date &&
                                (!currentEndDate || currentEndDate < date)
                              ) {
                                setValue(
                                  name('arrivalTimeRestrictionEnd'),
                                  isoString
                                )
                              }

                              if (isSameDayDelivery && date) {
                                const departureStartDate = safeParseISO(
                                  departureTimeStartWatch
                                )
                                if (
                                  departureStartDate &&
                                  date < departureStartDate
                                ) {
                                  setValue(
                                    name('departureTimeRestrictionStart'),
                                    null
                                  )
                                }
                              }

                              revalidateTimeRestrictions()
                            }}
                          />
                        </Form.Item>
                      )}
                    />
                  </div>
                  <span className="flex-none pt-7">〜</span>
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.END}</label>
                    <Controller
                      control={form.control}
                      name={name('arrivalTimeRestrictionEnd')}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                          label=""
                        >
                          <NormalizedTimePicker
                            format="HH:mm"
                            placeholder={LABELS.END_TIME}
                            showNow={false}
                            needConfirm={false}
                            allowClear={false}
                            className="w-full"
                            minuteStep={5}
                            value={safeParseISO(field.value)}
                            disabledTime={disabledArrivalTimeEnd}
                            onInvalidText={(msg) => {
                              setValue(
                                name('arrivalTimeRestrictionEnd'),
                                null,
                                {
                                  shouldValidate: false,
                                  shouldDirty: true
                                }
                              )
                              setError(
                                name('arrivalTimeRestrictionEnd'),
                                { type: 'manual', message: msg },
                                { shouldFocus: true }
                              )
                            }}
                            onValidText={() =>
                              clearErrors(name('arrivalTimeRestrictionEnd'))
                            }
                            onChange={(date) => {
                              field.onChange(date ? date.toISOString() : null)

                              revalidateTimeRestrictions()
                            }}
                          />
                        </Form.Item>
                      )}
                    />
                  </div>
                  {/* 削除ボタンコンテナ */}
                  <div className="mb-[1px] flex h-[32px] w-8 flex-none items-center justify-center">
                    {(arrivalTimeStartWatch || arrivalTimeEndWatch) && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        className="hover:bg-red-50 flex items-center justify-center p-1"
                        onClick={() => {
                          setValue(name('arrivalTimeRestrictionStart'), null)
                          setValue(name('arrivalTimeRestrictionEnd'), null)
                          revalidateTimeRestrictions()
                        }}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-end gap-2">
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.START}</label>
                    {sourceOrder?.arrivalTimeRestrictionStart ? (
                      <DateFormatter
                        value={sourceOrder.arrivalTimeRestrictionStart}
                        dateFormat="HH:mm"
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <label className="mb-1 text-xs">{LABELS.END}</label>
                    {sourceOrder?.arrivalTimeRestrictionEnd ? (
                      <DateFormatter
                        value={sourceOrder.arrivalTimeRestrictionEnd}
                        dateFormat="HH:mm"
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              )}
            </BasicTd>
          </BasicTr>
        </>
      )}
    </>
  )
}
